import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { locations, clinicPhone, clinicEmail, clinicInteriorImage } from '../mock';

const ContactPage = () => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', clinic: locations[0].slug, msg: '' });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');

    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => data.append(key, value));

      const response = await fetch('/contact.php', {
        method: 'POST',
        body: data,
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Message could not be sent.');
      }

      setSent(true);
      setForm({ name: '', email: '', phone: '', clinic: locations[0].slug, msg: '' });
    } catch (err) {
      setError(err.message || 'Message could not be sent. Please call us instead.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="App bg-white">
      <Header />
      <PageHero
        eyebrow="Get in Touch"
        title="Call, message, or"
        italic="drop by."
        description="Phone calls are preferred for appointment booking. Call +1-519-252-2752 and our reception team will help you find a time that works."
        image={clinicInteriorImage}
        breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Contact' }]}
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 grid lg:grid-cols-[1fr_420px] gap-14">
          {/* Form */}
          <div>
            <h2 className="text-[32px] lg:text-[42px] font-bold tracking-tight text-neutral-900">
              Send us a <span className="font-serif-italic font-normal">message</span>
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-neutral-600 max-w-lg">
              Fill in the form below and a member of our team will get back to you within one business day.
            </p>

            {sent ? (
              <div className="mt-10 p-8 rounded-2xl bg-[#fff1f3] border border-[#f5bcc8]">
                <h3 className="text-[20px] font-semibold text-neutral-900">Thanks, we&rsquo;ll be in touch.</h3>
                <p className="mt-2 text-[14px] text-neutral-700">
                  We have received your message. For appointment booking, please call us at {clinicPhone}.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-10 space-y-5 max-w-xl">
                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-800">
                    {error}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700">Full name</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] focus:border-[#CA0028] outline-none transition-colors" placeholder="Alex Rivera" />
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700">Email</label>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] focus:border-[#CA0028] outline-none transition-colors" placeholder="alex@email.com" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700">Phone</label>
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] focus:border-[#CA0028] outline-none transition-colors" placeholder="+1-519-252-2752" />
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-neutral-700">Preferred clinic</label>
                    <select value={form.clinic} onChange={(e) => setForm({ ...form, clinic: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] bg-white focus:border-[#CA0028] outline-none transition-colors">
                      {locations.map((l) => (
                        <option key={l.slug} value={l.slug}>{l.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[13px] font-medium text-neutral-700">How can we help?</label>
                  <textarea required rows={5} value={form.msg} onChange={(e) => setForm({ ...form, msg: e.target.value })} className="mt-2 w-full rounded-xl border border-neutral-200 px-4 py-3 text-[14px] focus:border-[#CA0028] outline-none transition-colors resize-none" placeholder="Tell us about your injury or the service you’re interested in…" />
                </div>
                <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-[#CA0028] hover:bg-[#A80021] disabled:bg-neutral-400 text-white text-[14px] font-medium pl-6 pr-2 py-2 rounded-full transition-colors">
                  {sending ? 'Sending...' : 'Send Message'}
                  <span className="w-8 h-8 grid place-items-center rounded-full bg-white/15"><Send size={13} /></span>
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <aside>
            <div className="rounded-2xl border border-neutral-200 p-7">
              <h3 className="text-[14px] font-semibold tracking-[0.15em] text-neutral-500 uppercase">Reach us directly</h3>
              <div className="mt-5 space-y-4 text-[15px] text-neutral-800">
                <p className="flex items-center gap-3"><Phone size={16} className="text-neutral-500" /> {clinicPhone}</p>
                <p className="flex items-center gap-3"><Mail size={16} className="text-neutral-500" /> {clinicEmail}</p>
                <p className="flex items-start gap-3"><MapPin size={16} className="mt-1 text-neutral-500" /> 960 Tecumseh Rd E, Windsor, ON N8X 2S6</p>
                <p className="flex items-center gap-3"><Clock size={16} className="text-neutral-500" /> Mon-Fri 8:00-17:00</p>
              </div>
            </div>
            <div className="mt-6 rounded-2xl overflow-hidden border border-neutral-200 aspect-[4/3] bg-neutral-100">
              <iframe title="Map" className="w-full h-full" src="https://www.google.com/maps?q=960+Tecumseh+Rd+E,+Windsor,+ON+N8X+2S6&output=embed" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
